// Frontend PostHog client. Mirrors the FreeTime pattern: lazy-loaded so
// SSR doesn't touch window, persistence on localStorage to honour the
// no-cookie-banner rule, traffic routed through the local /ingest
// reverse proxy so tracker blockers (Brave Shields, uBlock, ETP) can't
// blackhole the analytics.
//
// All exports gracefully no-op when POSTHOG_API_KEY is unset on the
// environment. That means the wiring can ship today and become live
// the moment the env var is set in Gadget production.

let posthogInstance: any = null;
let initialized = false;
let initStarted = false;

/**
 * Lazy-init PostHog on the client. Call from a useEffect; passing
 * null/undefined for `apiKey` skips init entirely (graceful no-op).
 *
 * `meta.environment` MUST be the Gadget environment name (read from
 * `gadgetConfig.environment.name` server-side and threaded through
 * the loader). Don't substitute NODE_ENV: Vite builds with
 * NODE_ENV=production for BOTH Gadget environments, so every dev
 * event self-reports as "production" and ruins the dashboard.
 */
export async function initPostHog(
  apiKey: string | null | undefined,
  meta: { environment: string; app: string } = {
    environment: "unknown",
    app: "manos",
  },
): Promise<void> {
  if (
    initialized ||
    initStarted ||
    typeof window === "undefined" ||
    !apiKey
  ) {
    return;
  }
  initStarted = true;
  try {
    const { default: posthog } = await import("posthog-js");
    // api_host MUST be an absolute URL. PostHog Toolbar refuses
    // relative values and falls back to internal-j.posthog.com for
    // retries -- which is exactly the host adblockers like uBlock
    // target most aggressively. Building from window.location.origin
    // keeps everything routed through our /ingest proxy and works
    // across manos.team, dev URLs, and any future custom domain.
    const apiHost =
      typeof window !== "undefined" && window.location?.origin
        ? `${window.location.origin}/ingest`
        : "/ingest";
    posthog.init(apiKey, {
      api_host: apiHost,
      // Set ui_host so PostHog can build dashboard links without
      // trying to derive them from the relative api_host. No direct
      // calls go to ui_host -- it's only used for "View in PostHog"
      // URLs in the dev console / inspect panel.
      ui_host: "https://eu.posthog.com",
      persistence: "localStorage",
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      rageclick: true,
      // Web Vitals: PostHog hooks into the web-vitals library to
      // capture LCP / INP / CLS / FCP / TTFB on every navigation.
      // Surfaces in the Web Vitals dashboard + Web Analytics. No
      // per-event setup required -- enabling it in init covers the
      // whole app.
      capture_performance: {
        web_vitals: true,
        network_timing: true,
      },
      // Error tracking: posthog-js auto-captures unhandled exceptions
      // and unhandled promise rejections as $exception events when
      // capture_exceptions is true. Combined with PostHog's Error
      // Tracking product (which groups them by stack signature), this
      // gives us a frontend Sentry-equivalent without a separate SDK.
      capture_exceptions: true,
      // Session replay needs the separate recorder.js bundle. With
      // api_host: "/ingest", posthog-js 1.353+ routes the bundle
      // fetch through /ingest/static/recorder.js, which our proxy
      // (web/routes/ingest.$.ts) forwards to eu-assets.i.posthog.com
      // -- staying on-domain and dodging tracker blockers without
      // losing the recorder.
      //
      // We do NOT set disable_external_dependency_loading here. That
      // flag would also disable the recorder bundle and was the reason
      // session replay didn't work previously. Modern posthog-js
      // honours api_host for sub-bundle ingest too, so the historical
      // concern about Toolbar / Surveys phoning home to hardcoded
      // hosts no longer applies for our config. If a tracker blocker
      // proves to still leak in the wild we'll revisit per-feature.
      session_recording: {
        // Mask form inputs but keep visible interactions recordable.
        // Defaults are sensible -- explicit so future readers don't
        // have to chase the lib's defaults.
        maskAllInputs: true,
        maskTextSelector: "[data-private]",
      },
      // `loaded` fires AFTER posthog.init() resolves but BEFORE the
      // first event is sent (autocapture/pageview included). Stamp
      // the global super-properties here so every event -- not just
      // ones captured after the next animation frame -- carries the
      // env + app tags. Doing it after init() returned was racy:
      // pageview/autocapture often fired tagless before register
      // landed, leaving 250+ events/day with environment=null.
      loaded: (ph: any) => {
        try {
          ph.register({ environment: meta.environment, app: meta.app });
        } catch {
          // Don't let a bad property write kill the SDK.
        }
      },
    } as any);
    posthogInstance = posthog;
    // Expose the SDK on window for ad-hoc dev console use ("flip my
    // is_internal off for a smoke test", etc). Doesn't change runtime
    // behaviour for end users; just makes posthog.register / capture /
    // setPersonProperties reachable without re-importing.
    if (typeof window !== "undefined") {
      (window as any).posthog = posthog;
    }
    initialized = true;
  } catch (err) {
    // Don't let analytics failures break the app. Most common cause:
    // an extension intercepts the dynamic import. Swallow + carry on.
    console.warn("[posthog] init failed", err);
    initStarted = false;
  }
}

/**
 * Report a caught exception to PostHog's Error Tracking. Use this for
 * errors you've handled in code paths where you want PostHog to know
 * about them (e.g. fetch failures with graceful fallback). Unhandled
 * errors are auto-captured via capture_exceptions; this is for the
 * "I caught it but want to know" case.
 *
 * Safe to call before init -- no-ops silently.
 */
export function reportError(
  error: unknown,
  context: Record<string, unknown> = {},
): void {
  if (!posthogInstance) return;
  try {
    if (typeof posthogInstance.captureException === "function") {
      posthogInstance.captureException(error, context);
    } else {
      // Fallback for older posthog-js -- emit a $exception event
      // shaped to match PostHog's Error Tracking schema.
      const err = error instanceof Error ? error : new Error(String(error));
      posthogInstance.capture("$exception", {
        $exception_message: err.message,
        $exception_type: err.name,
        $exception_stack_trace_raw: err.stack,
        ...context,
      });
    }
  } catch {
    // Last-resort no-op
  }
}

/**
 * Identify the current user. Safe to call before init -- the call
 * will no-op silently and any subsequent identify after init kicks
 * in normally.
 */
export function identifyUser(
  userId: string,
  properties: Record<string, unknown> = {},
): void {
  posthogInstance?.identify(userId, properties);
}

/** Capture a custom event. No-ops if PostHog isn't initialised. */
export function captureSignal(
  event: string,
  properties: Record<string, unknown> = {},
): void {
  posthogInstance?.capture(event, properties);
}

/** Reset the session (call on sign-out). */
export function resetAnalytics(): void {
  posthogInstance?.reset();
}

/** Direct access for advanced cases (feature flags, surveys, etc.). */
export function getPostHog(): any | null {
  return posthogInstance;
}
